import {
  Box,
  BUTTON_VARIANTS,
  Button,
  SPACING,
  Avatar,
  ChevronRightIcon,
  LoadingIndicator,
} from '@community_dev/pixels'
import type { JSX } from 'react'
import styled, { css, useTheme } from 'styled-components'

import { useUserContext } from 'contexts/UserProvider'
import { SimpleLayout } from 'layouts/SimpleLayout'
import { formatPhone } from 'utils/normalize'

type StyledButtonProps = {
  $active?: boolean
}

const StyledButton = styled.button<StyledButtonProps>`
  width: 100%;
  border: none;
  background: none;
  padding: ${SPACING[4]} 0;
  text-align: left;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme?.COLORS?.BORDERS};
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  ${({ $active }) =>
    !$active &&
    css`
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    `}
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 -40px;

  li {
    padding: 0 40px;

    &:hover {
      background: ${({ theme }) => theme?.COLORS?.DEPRECATED_HOVER};
    }

    &:last-child ${StyledButton} {
      border: none;
    }
  }

  ${Avatar} {
    margin-right: ${SPACING[2]};
  }

  p {
    margin: 0;
  }
`

const StyledDetails = styled.div`
  flex-grow: 1;
`

const StyledName = styled.p`
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.BODY1};
  font-weight: 600;
`

const StyledPhone = styled.p`
  color: ${({ theme }) => theme?.COLORS?.SUBTEXT};
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
  font-weight: 500;

  span {
    ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
    font-weight: 600;
    color: ${({ theme }) => theme?.COLORS?.LINKS};
  }
`

export function ClientSelection(): JSX.Element {
  const { COLORS } = useTheme() || {}
  const { contexts, setContext, logout } = useUserContext()

  return (
    <SimpleLayout>
      {contexts ? (
        <Box>
          <Box.Content>
            <h1>Select Account</h1>
            <StyledList>
              {contexts.map((context) => (
                <li key={context.clientId}>
                  <StyledButton
                    $active={context.isBillingActive}
                    onClick={() => {
                      setContext(context)
                    }}
                    type="button"
                  >
                    <Avatar name={context.name} src={context.clientImage} />
                    <StyledDetails>
                      <StyledName>{context.name}</StyledName>
                      {context.isBillingActive ? (
                        <StyledPhone>{formatPhone(context.phoneNumber)}</StyledPhone>
                      ) : (
                        <StyledPhone>
                          Inactive <span>Enter payment</span>
                        </StyledPhone>
                      )}
                    </StyledDetails>
                    <ChevronRightIcon color={COLORS?.SUBTEXT} />
                  </StyledButton>
                </li>
              ))}
            </StyledList>
          </Box.Content>
          <Box.Footer>
            <Button onClick={logout} variant={BUTTON_VARIANTS.LINK}>
              Logout
            </Button>
          </Box.Footer>
        </Box>
      ) : (
        <LoadingIndicator />
      )}
    </SimpleLayout>
  )
}
